import { useEffect, useState } from 'react';
import { api, getSession } from '../api/client.ts';

interface Ligne {
  id: string; csbNom: string; agent: string; agentEmail: string; source: string; module: string; statut: string; resume: string;
  periode: string | null; motifRejet: string | null; erreur: string | null; synchroniseLe: string; valideLe: string | null;
}
const LIBELLE: Record<string, string> = { SYNCHRONISE: 'Synchronisé · à valider', EN_ATTENTE: 'En cours de validation', VALIDE: 'Validé', REJETE: 'Rejeté' };
const date = (s: string | null) => (s ? new Date(s).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '');

/**
 * Responsable CSB / ADMIN : valider ou rejeter les saisies envoyées par les comptes Stock et Agent (rien n'entre dans les données
 * officielles avant validation). Comptes Stock / Agent : suivi de leurs propres envois.
 */
export default function Validation() {
  const role = getSession().role;
  const valideur = role === 'ADMIN' || role === 'RESPONSABLE_CSB';
  const [statut, setStatut] = useState('SYNCHRONISE');
  const [lignes, setLignes] = useState<Ligne[]>([]);
  const [choix, setChoix] = useState<Set<string>>(new Set());
  const [message, setMessage] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  async function charger() {
    setErreur(null);
    try {
      setLignes(await api.get<Ligne[]>(valideur ? `/validation?statut=${statut}` : '/validation/mes-saisies'));
      setChoix(new Set());
    } catch (e: any) { setErreur(e.message ?? 'Chargement impossible'); }
  }
  useEffect(() => { void charger(); }, [statut]);

  const basculer = (id: string) => setChoix((c) => { const n = new Set(c); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  async function validerChoix(ids: string[]) {
    if (!ids.length) return;
    setEnCours(true); setMessage(null); setErreur(null);
    try {
      const out: { resultats: Array<{ id: string; ok: boolean; erreur?: string }> } = { resultats: [] };
      for (let i = 0; i < ids.length; i += 50) { const r = await api.post<typeof out>('/validation/valider-lot', { ids: ids.slice(i, i + 50) }); out.resultats.push(...r.resultats); }
      const ko = out.resultats.filter((r) => !r.ok);
      setMessage(`${out.resultats.length - ko.length} saisie(s) validée(s) et intégrée(s) aux données officielles.${ko.length ? ` ${ko.length} refusée(s) : ${ko[0].erreur}` : ''}`);
    } catch (e: any) { setErreur(e.message ?? 'Validation impossible'); }
    setEnCours(false);
    await charger();
  }
  async function rejeter(id: string) {
    const motif = prompt('Motif du rejet (obligatoire, 3 caractères minimum) :');
    if (!motif) return;
    try { await api.post(`/validation/${id}/rejeter`, { motif }); setMessage('Saisie rejetée.'); } catch (e: any) { setErreur(e.message ?? 'Rejet impossible'); }
    await charger();
  }

  return (
    <div>
      <h1>{valideur ? 'Saisies à valider' : 'Mes saisies envoyées'}</h1>
      <p className="note">
        {valideur ? "Les saisies des comptes Stock et Agent restent en attente : elles n'entrent dans les données officielles (RMA, rapports, exports) qu'après votre validation."
          : "Vos saisies sont envoyées au responsable du CSB : elles comptent dans les données officielles une fois validées."}
      </p>
      {valideur && (
        <p>
          <label htmlFor="filtre-statut">Afficher : </label>
          <select id="filtre-statut" value={statut} onChange={(e) => setStatut(e.target.value)}>
            <option value="SYNCHRONISE">À valider</option><option value="VALIDE">Validées</option><option value="REJETE">Rejetées</option><option value="TOUS">Toutes</option>
          </select>{' '}
          <button className="secondaire" onClick={() => void charger()}>Actualiser</button>
        </p>
      )}
      {message && <p role="status">✅ {message}</p>}
      {erreur && <p className="messages-erreur">{erreur}</p>}
      {valideur && statut === 'SYNCHRONISE' && lignes.length > 0 && (
        <p>
          <button disabled={enCours || !choix.size} onClick={() => void validerChoix([...choix])}>Valider la sélection ({choix.size})</button>{' '}
          <button className="secondaire" disabled={enCours} onClick={() => void validerChoix(lignes.map((l) => l.id))}>Tout valider ({lignes.length})</button>
        </p>
      )}
      {lignes.length === 0 ? <p>Aucune saisie.</p> : (
        <div style={{ overflowX: 'auto' }}>
          <table>
            <thead><tr>{valideur && statut === 'SYNCHRONISE' && <th />}<th>Saisie</th><th>Mois</th><th>Envoyée par</th><th>Envoi</th><th>Statut</th>{valideur && <th />}</tr></thead>
            <tbody>
              {lignes.map((l) => (
                <tr key={l.id}>
                  {valideur && statut === 'SYNCHRONISE' && <td><input type="checkbox" aria-label={`Sélectionner ${l.resume}`} checked={choix.has(l.id)} onChange={() => basculer(l.id)} /></td>}
                  <td>{l.resume}{l.erreur && <div className="messages-erreur">⚠ {l.erreur}</div>}{l.motifRejet && <div className="note">Motif : {l.motifRejet}</div>}</td>
                  <td>{l.periode ?? ''}</td><td>{l.agent}</td><td>{date(l.synchroniseLe)}</td><td>{LIBELLE[l.statut] ?? l.statut}</td>
                  {valideur && <td>{l.statut === 'SYNCHRONISE' && <><button disabled={enCours} onClick={() => void validerChoix([l.id])}>Valider</button>{' '}<button className="secondaire" disabled={enCours} onClick={() => void rejeter(l.id)}>Rejeter</button></>}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
